import React, { Fragment, useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Badge, Button, FormGroup, Input } from 'reactstrap';
import { useHelpUIContext } from '../HelpUIProvider';
import HelpTableColumns, { HelpColumns } from './HelpTableColumns';
import { HelpTableSearchBar } from './HelpTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getHelpList, setHelpFilters, toggleCheckedItemById } from '../../redux/actions';
import EyeIcon from 'mdi-react/EyeIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';
import moment from 'moment';

const HelpTable = () => {
  const helpUIContext = useHelpUIContext();
  const dispatch = useDispatch();
  const {
    data: helps,
    error,
    filters,
    loading,
    totalRecords,
    checkedHelpIds,
  } = useSelector((state) => state.cadsAIHelps, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [columns, setColumns] = useState(0);

  useEffect(() => {
    setColumns(HelpColumns.length);
  }, [HelpColumns.length]);

  React.useEffect(() => {
    dispatch(getHelpList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setHelpFilters({
        sortBy: property,
        isSortOrderDescending: !isSortOrderDescending,
      }),
    );
  };

  const renderActionButtons = (helpId) => (
    <div className="actions">
      <Button
        className="icon mb-0"
        color="success"
        outline
        onClick={() => helpUIContext.openViewHelpDialog(helpId)}
      >
        <p>
          <EyeIcon /> View
        </p>
      </Button>
    </div>
  );

  const handleChangePage = (event, currentPage) => {
    dispatch(setHelpFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setHelpFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows = page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - helps.length : 0;

  return (
    <Fragment>
      <div className="material-table__wrap">
        <HelpTableSearchBar />
        <Table className="material-table">
          <HelpTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={columns}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading helps...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!helps?.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={columns} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No Helps'
                    : 'No helps with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {helps?.map((help) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={help?.id}>
                  <TableCell align="center" className="material-table__cell">
                    <FormGroup>
                      <Input
                        type="checkbox"
                        value={help?.id}
                        defaultChecked={checkedHelpIds.includes(help?.id)}
                        onChange={(e) => dispatch(toggleCheckedItemById(+e.target.value))}
                      />
                    </FormGroup>
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {help?.name}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {help.email}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {help.inquiredOn ? moment(help.inquiredOn).format('DD/MM/yyyy') : '-'}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    <Badge
                      color={`${help?.status ? 'primary' : 'secondary'}`}
                      style={{ marginRight: 24 }}
                    >
                      {help?.status ?? 'NA'}
                    </Badge>
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {help.subject}
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: 250 }}
                  >
                    {renderActionButtons(help?.id)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={columns} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default HelpTable;
